import React, { useState } from "react";
import { Card, Col, OverlayTrigger, Placeholder, Row, Toast, ToastContainer, Tooltip } from "react-bootstrap";
import { Link } from "react-router-dom";

import DstButton from "../../../../components/DstButton/DstButton";
import DstConfirm from "../../../../components/DstConfirm/DstConfirm";
import infoRow from "../../../../components/InfoRow/InfoRow";
import { PermissionComponent } from "../../../../services/PermissionGuard";
import { formatDateHumanly } from "../../../../services/TimeService";
import translate from "../../../../services/Translate";
import { ModuleEvaluationsProps } from "./ModuleEvaluations.d";
import { unlinkEvaluationFromModule } from "./ModuleEvaluations.function";
import LinkModuleEvaluationModal from "./components/LinkModuleEvaluationModal";

const ModuleEvaluations = ({
  moduleId,
  language,
  evaluations,
  evaluationsLoaded,
  onAddSuccess,
}: ModuleEvaluationsProps) => {
  const [showCopyToast, setShowCopyToast] = useState(false);
  const [showAddSuccessToast, setShowAddSuccessToast] = useState(false);
  const [showModalAddEvaluation, setShowModalAddEvaluation] = useState(false);
  const [showModalUnlinkEvaluation, setShowModalUnlinkEvaluation] = useState(false);
  const [evaluationToUnlinkId, setEvaluationToUnlinkId] = useState<number | null>(null);
  const [, setUnlinkingEvaluationLoading] = useState(false);

  return (
    <div className="col-lg">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0 m-bold">{translate(language, "PAGES.MODULE.EVALUATIONS.TITLE")}</h3>
        <DstButton
          btnImageBefore={<span className="dst-icon-file-plus-02 me-2" />}
          value={translate(language, "PAGES.MODULE.EVALUATIONS.ADD")}
          clickFunction={() => setShowModalAddEvaluation(true)}
        />
      </div>

      {!evaluationsLoaded ? (
        <div className="mt-3">
          {[...Array(2)].map((_, i) => (
            <Card key={i} className="mb-3 shadow-sm">
              <Card.Body>
                <Placeholder as="p" animation="glow" className="mb-2">
                  <Placeholder xs={6} />
                </Placeholder>
                <Placeholder as="p" animation="glow" className="mb-0">
                  <Placeholder xs={4} />
                </Placeholder>
              </Card.Body>
            </Card>
          ))}
        </div>
      ) : evaluations.length === 0 ? (
        <div className="text-muted mt-3">{translate(language, "PAGES.MODULE.EVALUATIONS.NONE")}</div>
      ) : (
        evaluations.map((evaluation: any) => (
          <Card key={evaluation.id} className="mb-4 border">
            <Card.Body>
              <div className="d-flex align-items-start justify-content-between mb-3">
                <div>
                  <h4 className="mb-1 fw-bold">{evaluation.name}</h4>
                  {evaluation.description && <div className="text-muted small">{evaluation.description}</div>}
                </div>
                <div className="d-flex gap-2 ms-3">
                  <Link to={`/evaluation/${evaluation.id}`} className="btn btn-sm btn-secondary">
                    {translate(language, "PAGES.MODULE.EVALUATIONS.LINK")}
                  </Link>
                  <PermissionComponent neededPermission={[{ methods: ["DELETE"], route: "/modules/*/evaluations/*" }]}>
                    <DstButton
                      variant="danger"
                      btnSize="sm"
                      value={translate(language, "PAGES.MODULE.EVALUATIONS.UNLINK")}
                      clickFunction={() => {
                        setEvaluationToUnlinkId(evaluation.id);
                        setShowModalUnlinkEvaluation(true);
                      }}
                    />
                  </PermissionComponent>
                </div>
              </div>
              <Row>
                <Col md={6}>
                  {infoRow(
                    "dst-icon-hash-01",
                    translate(language, "PAGES.MODULE.EVALUATIONS.FIELD_ID"),
                    evaluation.id,
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id={`copy-tooltip-${evaluation.id}`}>
                          {translate(language, "PAGES.MODULE.EVALUATIONS.COPY_ID")}
                        </Tooltip>
                      }
                    >
                      <span
                        className="dst-icon-copy-01 clickable"
                        onClick={() => {
                          navigator.clipboard.writeText(evaluation.id);
                          setShowCopyToast(true);
                        }}
                      />
                    </OverlayTrigger>
                  )}
                  {infoRow(
                    "dst-icon-file-code-01",
                    translate(language, "PAGES.MODULE.EVALUATIONS.FIELD_TYPE"),
                    evaluation.type
                  )}

                  {infoRow(
                    "dst-icon-code-01",
                    translate(language, "PAGES.MODULE.EVALUATIONS.FIELD_LANGUAGE_PROGRAMMING"),
                    evaluation.programming_language
                  )}

                  {infoRow(
                    "dst-icon-clock",
                    translate(language, "PAGES.MODULE.EVALUATIONS.FIELD_DURATION"),
                    evaluation.duration
                      ? `${evaluation.duration} ${translate(language, "PAGES.MODULE.EVALUATIONS.MINUTES")}`
                      : "N/A"
                  )}

                  {infoRow(
                    "dst-icon-globe-01",
                    translate(language, "PAGES.MODULE.EVALUATIONS.FIELD_LANGUAGE"),
                    evaluation.language
                  )}

                  {infoRow(
                    "dst-icon-refresh-ccw-02",
                    translate(language, "PAGES.MODULE.EVALUATIONS.FIELD_ALLOW_REPASS"),
                    evaluation.allow_repass
                      ? translate(language, "PAGES.MODULE.EVALUATIONS.YES")
                      : translate(language, "PAGES.MODULE.EVALUATIONS.NO")
                  )}

                  {infoRow(
                    "dst-icon-alert-triangle",
                    translate(language, "PAGES.MODULE.EVALUATIONS.FIELD_IS_DEPRECATED"),
                    evaluation.is_deprecated
                      ? translate(language, "PAGES.MODULE.EVALUATIONS.YES")
                      : translate(language, "PAGES.MODULE.EVALUATIONS.NO")
                  )}
                </Col>

                <Col md={6}>
                  {infoRow(
                    "dst-icon-check-verified-01",
                    translate(language, "PAGES.MODULE.EVALUATIONS.FIELD_CORRECTION_MODULE"),
                    evaluation.correction_module?.name
                  )}

                  {infoRow(
                    "dst-icon-layers-three-01",
                    translate(language, "PAGES.MODULE.EVALUATIONS.FIELD_PROGRAM"),
                    evaluation.program?.name
                  )}
                  {infoRow(
                    "dst-icon-database-01",
                    translate(language, "PAGES.MODULE.EVALUATIONS.FIELD_S3_NAME"),
                    evaluation.s3_name
                  )}

                  {infoRow(
                    "dst-icon-user-01",
                    translate(language, "PAGES.MODULE.EVALUATIONS.FIELD_OWNER"),
                    evaluation.owner ? (
                      <Link to={`/user/${evaluation.owner.id}`}>
                        {evaluation.owner.first_name} {evaluation.owner.last_name}
                      </Link>
                    ) : (
                      "N/A"
                    )
                  )}

                  {infoRow(
                    "dst-icon-bar-chart-01",
                    translate(language, "PAGES.MODULE.EVALUATIONS.FIELD_SUCCESS_RATIO"),
                    <div className="d-flex align-items-center">
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>{translate(language, "PAGES.MODULE.EVALUATIONS.EXPLAIN_SUCCESS_RATIO")}</Tooltip>
                        }
                      >
                        <span className="dst-icon-info-circle clickable me-2" />
                      </OverlayTrigger>
                      {evaluation.success_ratio !== null ? `${(evaluation.success_ratio * 100).toFixed(0)}%` : "N/A"}
                    </div>
                  )}

                  {infoRow(
                    "dst-icon-calendar-plus-02",
                    translate(language, "PAGES.MODULE.EVALUATIONS.FIELD_CREATED_AT"),
                    formatDateHumanly(language, evaluation.dt_created)
                  )}

                  {infoRow(
                    "dst-icon-calendar-check-02",
                    translate(language, "PAGES.MODULE.EVALUATIONS.FIELD_UPDATED_AT"),
                    formatDateHumanly(language, evaluation.dt_updated)
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        ))
      )}

      <LinkModuleEvaluationModal
        moduleId={moduleId}
        language={language}
        show={showModalAddEvaluation}
        onClose={() => setShowModalAddEvaluation(false)}
        onSuccess={() => {
          setShowAddSuccessToast(true);
          onAddSuccess();
        }}
      />
      <DstConfirm
        show={showModalUnlinkEvaluation}
        onHide={() => setShowModalUnlinkEvaluation(false)}
        onConfirm={() => {
          unlinkEvaluationFromModule(moduleId, evaluationToUnlinkId!, language, setUnlinkingEvaluationLoading, () => {
            setShowModalUnlinkEvaluation(false);
            onAddSuccess();
          });
        }}
        title={translate(language, "PAGES.MODULE.EVALUATIONS.UNLINK_EVALUATION_POPUP.TITLE")}
        messageConfirm={translate(language, "PAGES.MODULE.EVALUATIONS.UNLINK_EVALUATION_POPUP.CONFIRM")}
        confirmTextButton={translate(language, "PAGES.MODULE.EVALUATIONS.UNLINK_EVALUATION_POPUP.BUTTON")}
      />

      <ToastContainer position="top-end" className="p-3">
        <Toast bg="success" show={showCopyToast} onClose={() => setShowCopyToast(false)} delay={1500} autohide>
          <Toast.Body className="text-white">{translate(language, "PAGES.MODULE.EVALUATIONS.SUCCESS_COPY")}</Toast.Body>
        </Toast>
        <Toast
          bg="success"
          show={showAddSuccessToast}
          onClose={() => setShowAddSuccessToast(false)}
          delay={3000}
          autohide
        >
          <Toast.Body className="text-white">
            {translate(language, "PAGES.MODULE.EVALUATIONS.ADD_MODAL.SUCCESS")}
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default ModuleEvaluations;
